<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import LineCheck from '../../icons/LineCheck.vue'
import { classArray } from '../../utils/class'
import type { ComplexClassName } from '../../types/common'

interface Props {
  modelValue?: boolean
  disabled?: boolean
  name?: string
  label?: string
  labelClass?: ComplexClassName
  size?: 'sm' | 'md'
  description?: string
  checkClass?: ComplexClassName
}

const props = withDefaults(defineProps<Props>(), {
  labelClass: '',
  size: 'md',
  checkClass: '',
})

const emit = defineEmits<{ (e: 'update:modelValue', event: Props['modelValue']): void }>()

const id = `form-check-${crypto.randomUUID()}`
const localChecked = ref(props.modelValue)

const classesForLabel = computed((): string[] => {
  const cls = []
  if (props.disabled)
    cls.push('text-gray-300')

  if (props.size === 'sm')
    cls.push('text-sm leading-snug')

  return cls.concat(classArray(props.labelClass))
})

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-4.5 w-4.5'
    case 'md':
    default:
      return 'h-6 w-6'
  }
})

const iconClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'h-3 w-3 [&>path]:stroke-[2.5]'
    case 'md':
    default:
      return 'h-4 w-4'
  }
})

const descriptionClass = computed(() => {
  const cls = ['select-none']
  if (props.disabled)
    cls.push('text-gray-300')
  else
    cls.push('text-gray-600')

  if (props.size === 'sm')
    cls.push('text-xs ml-4.5 pl-2')
  else
    cls.push('text-sm ml-6 pl-3')

  return cls
})

const checkClass = computed(() => {
  const cls = []
  if (props.disabled) {
    return [
      'bg-gray-200',
      'border-gray-200',
      'cursor-default',
    ].concat(
      localChecked.value ? ['text-white'] : ['text-transparent'],
    ).concat(sizeClass.value).concat(classArray(props.checkClass))
  }
  if (!localChecked.value) {
    cls.push(
      'bg-white border-gray-400 text-transparent',
    )
  }
  else {
    cls.push(
      'bg-gray-800 border-gray-800 text-white',
    )
  }

  if (!localChecked.value)
    cls.push('focus-within:border-gray-800 group-hover/check:border-gray-800')

  else
    cls.push('ring-offset-2 ring-1 ring-transparent focus-within:ring-gray-800 group-hover/check:ring-gray-800')

  cls.push(sizeClass.value)
  cls.push('cursor-pointer')

  return cls.concat(classArray(props.checkClass))
})

const onChange = (checked: Props['modelValue']) => {
  emit('update:modelValue', checked)
}

watch(() => props.modelValue, (v) => {
  localChecked.value = v
})

watch(localChecked, (v) => {
  if (props.modelValue === null || props.modelValue !== v)
    onChange(v)
})
</script>

<template>
  <div class="relative inline-block">
    <!-- eslint-disable-next-line tailwindcss/no-custom-classname -->
    <label
      :for="id"
      class="group/check flex w-max items-start"
      :class="size === 'sm' ? 'gap-2' : 'gap-3'"
      data-testid="label"
    >
      <span
        class="relative flex shrink-0 items-center justify-center rounded-md border border-gray-300"
        :class="checkClass"
      >
        <input
          :id="id"
          v-model="localChecked"
          type="checkbox"
          role="checkbox"
          :aria-checked="localChecked"
          class="sr-only absolute inset-0 size-full cursor-pointer appearance-none bg-transparent disabled:cursor-default"
          :disabled="disabled"
          :name="name"
        >
        <LineCheck
          class="stroke-current font-semibold"
          :class="iconClass"
        />
      </span>
      <slot name="label">
        <span
          v-if="label"
          class="inline-flex w-max shrink-0 select-none items-center"
          :class="classesForLabel"
        >
          {{ label }}
        </span>
      </slot>
    </label>
    <div
      v-if="description"
      :class="descriptionClass"
    >
      {{ description }}
    </div>
  </div>
</template>
